/**
 * Campañas (`/activa/campanas`): una sola lista con todas las campañas del comercio (pedido de Alan del 28/10/2026).
 * Hasta ahora «Campañas» aparecía tres veces en el menú y cada mundo tenía su lista: las del WhatsApp oficial, las con
 * segmentación («Nueva campaña»), las por QR y las de mail de Snov. Acá van juntas, con la misma forma (el backend arma
 * cada fila con lo que ya dice la lista de su mundo: `GET /activa/campanas`).
 *
 * - Filtros por tipo (el mundo), estado, canal y período, y un buscador; todos en la URL, así un link lleva a la lista
 *   ya filtrada. Con un tipo elegido, el link a su propia lista, que sigue existiendo.
 * - Cada campaña: su nombre (lleva a su detalle), sus chips (tipo, canales, estado, «Seguimiento», «Se repite»), las
 *   fechas, el envío y el resultado con la misma cuenta que su detalle.
 * - «Nueva campaña» (del equipo) lleva a elegir el tipo (`/activa/campanas/nueva`, como el botón del menú).
 * - «Lista | Calendario» (29/10/2026) pasa al calendario de las mismas campañas (`/activa/campanas/calendario`).
 * - Una persona de un cliente ve lo que ya veía de cada mundo: sin las campañas con segmentación ni «Nueva campaña».
 *
 * En el celular cada campaña es una tarjeta que envuelve: nada se corta ni ensancha la página.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CornerDownRight, Layers, Plus, Repeat, Search, Undo2 } from "lucide-react";
import { Aviso, EncabezadoPagina, EstadoVacio, Pagina } from "../../../components/activa/Pagina";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { usePedido } from "../../emails/hooks/usePedido";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { campanasApi } from "../api";
import { VistaCampanas } from "../components/VistaCampanas";
import {
  CANAL,
  CANALES,
  ESTADO,
  ESTADOS,
  MUNDO,
  MUNDOS,
  PERIODO,
  PERIODOS,
  R_NUEVA,
  detalleDelEstado,
  filtrosDeLaUrl,
  hayFiltros,
  textoEnvio,
  textoFechas,
  textoResultado,
  urlDeLosFiltros,
} from "../logic/campanas";
import type { FilaCampana, FiltrosCampanas, ListaCampanas, Mundo } from "../types";

/** Lo que se espera después de la última tecla para buscar (como Público). */
export const ESPERA_BUSQUEDA_MS = 350;

const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold";
const selector = "h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

export default function CampanasPage() {
  const equipo = useEsEquipoDeenex();
  const [sp, setSp] = useSearchParams();
  const filtros = useMemo(() => filtrosDeLaUrl(sp), [sp]);
  const clave = urlDeLosFiltros(filtros).toString();
  const lista = usePedido<ListaCampanas>(() => campanasApi.listar(filtros), `campanas?${clave}`);
  const datos = lista.dato;
  // los tipos que ve quien mira: los dice el backend; mientras tanto, por su rol (un cliente no ve las con segmentación)
  const mundos: Mundo[] = datos?.mundos ?? MUNDOS.filter((m) => equipo || m !== "segmentacion");

  const cambiar = useCallback(
    (cambios: Partial<FiltrosCampanas>) => {
      // cualquier filtro nuevo vuelve a la primera página
      setSp((prev) => urlDeLosFiltros({ ...filtrosDeLaUrl(prev), pagina: undefined, ...cambios }), { replace: true });
    },
    [setSp],
  );

  // Busca mientras se escribe, como Público; con Enter, enseguida.
  const [busqueda, setBusqueda] = useState(filtros.q || "");
  useEffect(() => {
    const q = busqueda.trim();
    if (q === (filtros.q || "")) return;
    const id = setTimeout(() => cambiar({ q: q || undefined }), ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(id);
  }, [busqueda, filtros.q, cambiar]);
  // la URL manda (atrás, adelante, «Sacar los filtros»): el cuadro muestra lo que se está buscando
  useEffect(() => setBusqueda(filtros.q || ""), [filtros.q]);
  const buscar = (e: FormEvent) => {
    e.preventDefault();
    cambiar({ q: busqueda.trim() || undefined });
  };

  const conFiltros = hayFiltros(filtros);
  return (
    <Pagina>
      <EncabezadoPagina
        antetitulo="Campañas"
        titulo="Todas tus campañas"
        bajada="Las del WhatsApp oficial, las con segmentación, las por QR y las de mail, en un solo lugar."
        acciones={
          <>
            <VistaCampanas actual="lista" canal={filtros.canal} />
            {equipo && <NuevaCampana />}
          </>
        }
      />

      <div className="space-y-3" data-testid="filtros-campanas">
        {/* El tipo de campaña (su mundo): botones, porque son pocos y se cambian seguido */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Tipo de campaña">
          <BotonFiltro activo={!filtros.mundo} onClick={() => cambiar({ mundo: undefined })}>
            Todas
          </BotonFiltro>
          {mundos.map((m) => (
            <BotonFiltro key={m} activo={filtros.mundo === m} onClick={() => cambiar({ mundo: m })}>
              {MUNDO[m].label}
            </BotonFiltro>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={buscar} role="search" className="relative w-full sm:w-auto sm:min-w-[240px] sm:flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre"
              aria-label="Buscar campañas por nombre"
              className="w-full h-10 rounded-lg border border-slate-200 pl-8 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
            />
          </form>
          <select aria-label="Estado" value={filtros.estado || ""} onChange={(e) => cambiar({ estado: (e.target.value || undefined) as FiltrosCampanas["estado"] })} className={selector}>
            <option value="">Cualquier estado</option>
            {ESTADOS.map((x) => (
              <option key={x} value={x}>
                {ESTADO[x].texto}
              </option>
            ))}
          </select>
          <select aria-label="Canal" value={filtros.canal || ""} onChange={(e) => cambiar({ canal: (e.target.value || undefined) as FiltrosCampanas["canal"] })} className={selector}>
            <option value="">Cualquier canal</option>
            {CANALES.map((x) => (
              <option key={x} value={x}>
                {CANAL[x]}
              </option>
            ))}
          </select>
          <select aria-label="Período" value={filtros.periodo || "todo"} onChange={(e) => cambiar({ periodo: e.target.value === "todo" ? undefined : (e.target.value as FiltrosCampanas["periodo"]) })} className={selector}>
            {PERIODOS.map((x) => (
              <option key={x} value={x}>
                {PERIODO[x]}
              </option>
            ))}
          </select>
          {conFiltros && (
            <button type="button" onClick={() => setSp(new URLSearchParams(), { replace: true })} className="min-h-[40px] px-2 text-sm font-semibold text-violet-700 hover:text-violet-900">
              Sacar los filtros
            </button>
          )}
        </div>
        {/* Las listas de cada mundo siguen existiendo: con un tipo elegido, el link a la suya */}
        {filtros.mundo && (
          <Link to={MUNDO[filtros.mundo].lista} className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-900" data-testid="lista-del-mundo">
            {MUNDO[filtros.mundo].textoLista}
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        )}
      </div>

      {(datos?.avisos ?? []).map((a) => (
        <Aviso key={`${a.mundo}-${a.codigo}`} tono={a.codigo === "NO_DISPONIBLE" ? "error" : "atencion"} testId={`aviso-${a.mundo}`}>
          {a.texto}
        </Aviso>
      ))}

      {lista.error && !datos ? (
        <ErrorTarjeta texto={lista.error} onReintentar={lista.recargar} />
      ) : !datos ? (
        <Esqueleto alto="h-64" etiqueta="Cargando las campañas" />
      ) : datos.total === 0 ? (
        conFiltros ? (
          <EstadoVacio
            icono={Search}
            titulo="Ninguna campaña con esos filtros."
            testId="sin-campanas"
            accion={
              <button type="button" onClick={() => setSp(new URLSearchParams(), { replace: true })} className={btnSecundario}>
                <Undo2 size={16} aria-hidden="true" />
                Sacar los filtros
              </button>
            }
          />
        ) : (
          <EstadoVacio icono={Layers} titulo="Todavía no hay campañas." testId="sin-campanas">
            {equipo ? "Con «Nueva campaña» elegís qué tipo armar: cuando salga, la vas a ver acá con su resultado." : "Las arma el equipo de Deenex: cuando haya una, la vas a ver acá."}
          </EstadoVacio>
        )
      ) : (
        <>
          {lista.error && <p className="text-xs text-amber-700">{lista.error} Se muestra lo último que trajimos.</p>}
          <ul className="space-y-3" aria-label="Campañas">
            {datos.filas.map((f) => (
              <FilaDeCampana key={f.clave} f={f} equipo={equipo} />
            ))}
          </ul>
          <Paginas datos={datos} irA={(pagina) => cambiar({ pagina })} />
        </>
      )}
    </Pagina>
  );
}

function BotonFiltro({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={`min-h-[40px] rounded-full border px-3.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
        activo ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function FilaDeCampana({ f, equipo }: { f: FilaCampana; equipo: boolean }) {
  const estado = ESTADO[f.estado];
  const detalleEstado = detalleDelEstado(f);
  const fechas = textoFechas(f);
  const envio = textoEnvio(f);
  const resultado = textoResultado(f.resultado);
  return (
    <li data-testid={`campana-${f.clave}`} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <Link to={f.link} className="block font-semibold text-slate-900 break-words hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 rounded">
            {f.nombre || "Sin nombre"}
          </Link>
          {f.detalle && <p className="text-sm text-slate-500 break-words">{f.detalle}</p>}
        </div>
        <span className={`${chip} ${estado.cls} text-xs px-2.5 py-1`} data-testid="estado">
          {estado.texto}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="chips">
        <span className={`${chip} ${MUNDO[f.mundo].cls}`}>{MUNDO[f.mundo].label}</span>
        {f.canales.map((c) => (
          <span key={c} className={`${chip} bg-slate-100 text-slate-700`}>
            {CANAL[c]}
          </span>
        ))}
        {f.seguimientoDe && (
          <span className={`${chip} bg-indigo-50 text-indigo-700`} title={`Seguimiento de «${f.seguimientoDe.nombre}»`}>
            <CornerDownRight size={10} aria-hidden="true" /> Seguimiento
          </span>
        )}
        {f.seRepite && (
          <span className={`${chip} bg-violet-50 text-violet-700`} title={f.seRepite.texto}>
            <Repeat size={10} aria-hidden="true" /> {f.seRepite.pausada ? "Repetición en pausa" : "Se repite"}
          </span>
        )}
        {f.repeticionDe && <span className={`${chip} bg-violet-50 text-violet-700`}>Repetición {num(f.repeticionDe.n)}</span>}
        {f.simulada && <span className={`${chip} bg-amber-50 text-amber-800`}>Simulada</span>}
      </div>

      <div className="space-y-0.5 text-sm text-slate-600">
        {(detalleEstado || fechas) && <p className="break-words">{[detalleEstado, fechas].filter(Boolean).join(" · ")}</p>}
        {envio && <p>{envio}</p>}
        {f.seguimientoDe && <p className="text-slate-500 break-words">Seguimiento de «{f.seguimientoDe.nombre}»</p>}
        {f.repeticionDe && <p className="text-slate-500 break-words">Repetición {num(f.repeticionDe.n)} de «{f.repeticionDe.nombre}»</p>}
        {f.parteDe && equipo && (
          <p className="text-slate-500 break-words">
            Es el envío de{" "}
            <Link to={f.parteDe.link} className="font-semibold text-violet-700 hover:text-violet-900">
              {f.parteDe.nombre || "su campaña con segmentación"}
            </Link>
          </p>
        )}
      </div>

      <p className={`text-sm break-words ${resultado.hay ? "font-semibold text-emerald-800" : "text-slate-500"}`} data-testid="resultado">
        {resultado.texto}
      </p>
    </li>
  );
}

function Paginas({ datos, irA }: { datos: ListaCampanas; irA: (pagina: number) => void }) {
  return (
    <nav aria-label="Páginas de campañas" className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
      <span>
        {num(datos.total)} {plural(datos.total, "campaña", "campañas")}
        {datos.paginas > 1 && ` · página ${num(datos.pagina)} de ${num(datos.paginas)}`}
      </span>
      {datos.paginas > 1 && (
        <span className="flex gap-2">
          <button type="button" className={btnSecundario} disabled={datos.pagina <= 1} onClick={() => irA(datos.pagina - 1)}>
            Anteriores
          </button>
          <button type="button" className={btnSecundario} disabled={datos.pagina >= datos.paginas} onClick={() => irA(datos.pagina + 1)}>
            Siguientes
          </button>
        </span>
      )}
    </nav>
  );
}

/** «Nueva campaña»: lleva a elegir el tipo (`R_NUEVA`), el mismo lugar al que lleva el botón del menú. */
function NuevaCampana() {
  return (
    <Link to={R_NUEVA} className={btnPrimario} data-testid="nueva-campana-lista">
      <Plus size={16} aria-hidden="true" />
      Nueva campaña
    </Link>
  );
}
